import React, { useState, useRef, useEffect, ReactElement } from 'react';
import styled from '@emotion/styled';
import StatusIcon from '../atoms/icons/StatusIcon';
import { getBorders, getStatusColor, getTextColor, getInputTheme } from '../style';
import InputLabel from './InputLabel';
import { isDarkMode } from '../utils/helpers';
import { InputV2 } from 'swui';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { IInputStyle, ITextInputProps } from './types/TextInput';
import { ITooltip } from './types';
import { FONT_FAMILIES } from 'theme/material/constants';

const Input = styled('div')({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  width: '100%',
  '> div:first-of-type': {
    width: '100% !important',
  },
});

const getDefaultBorder = (islogin: string, joinForm: string, status: string | null) => {
  if (islogin === 'true') {
    return '1px solid rgba(0,0,0,0.15)';
  } else if (joinForm === 'true' && status === 'error') {
    return '2px solid #E21D4A';
  } else if (status) {
    return `1px solid ${getStatusColor()[status]}`;
  }

  return getBorders().DARKER;
};

const getFocusBorder = (joinForm: string, status: string | null) => {
  if (joinForm === 'true' && status === 'error') {
    return '2px solid #E21D4A';
  } else if (joinForm === 'true') {
    return getBorders().DARKER;
  } else if (status) {
    return `1px solid ${getStatusColor()[status]}`;
  }
  return getBorders().FOCUS;
};

const InputStyle = styled(InputV2)<IInputStyle>(
  () => ({
    color: getInputTheme().textInput,
    '&:focus': {
      color: getInputTheme().textInput + ' !important',
      background: getInputTheme().backgroundInput + ' !important',
    },
    '&::placeholder': {
      color: (isDarkMode() ? '#6A6A6A' : '#C9C9C9') + ' !important',
    },
  }),
  ({ islogin, joinForm, status }) => ({
    width: '100%',
    appearance: 'none',
    boxSizing: 'border-box',
    padding: '0 15px',
    height: '40px !important',
    outline: 'none',
    borderRadius: '3px !important',
    background: `${getInputTheme().backgroundInput} !important`,
    border: getDefaultBorder(islogin, joinForm, status) + ' !important',
    transition: 'border-color: .2s ease-in-out',
    fontSize: joinForm === 'true' ? 15 : 16,
    fontFamily: FONT_FAMILIES.primary + ' !important',
    boxShadow: '1px 1px 3px 0px rgba(0, 0, 0, 0.2) inset',
    '&:focus': {
      border: getFocusBorder(joinForm, status) + ' !important',
      color: islogin === 'true' ? '#000000 !important' : getInputTheme().textInput + '!important',
    },
  }),
  ({ borderless }) =>
    borderless === 'true' && {
      border: 'none !important',
    },
  ({ style }) => ({ ...style })
);

const StatusDescription = styled('div')<{ status: string }>(
  {
    marginTop: 4,
  },
  ({ status }) =>
    status && {
      color: getStatusColor()[status],
    }
);

const ShowPassword = styled('div')`
  position: absolute;
  right: 0;
  top: 0;
  cursor: pointer;
  // color: #3e79a5;
  color: #c9c9c9;
  font-weight: 500;
`;

const Counter = styled('div')<{ hasError: boolean }>(
  {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '16px',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  ({ hasError }) => ({
    color: !hasError ? (isDarkMode() ? '#AFB0B1' : '#595959') : getStatusColor().error,
  })
);

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const LabelContainer = styled('div')(() => ({
  position: 'relative',
  // marginBottom: '10px',
  minHeight: '17px',
  color: getTextColor().DARK.color,
}));

const InputContainer = styled('div')`
  padding-bottom: 24px;
`;

const StatusIconContainer = styled('div')`
  position: absolute;
  right: 10px;
`;

const renderStatusIcon = (status: string) => {
  return (
    <StatusIconContainer>
      <StatusIcon status={status} />
    </StatusIconContainer>
  );
};

const renderInputLabel = (
  inputLabel: string,
  labelDescription?: string,
  tooltip?: ITooltip,
  inputId?: string,
  isBilling?: boolean,
  customStyleLabel?: React.CSSProperties,
  isRequired?: boolean,
  extraLabelInfo?: React.ReactNode,
  customStyleLabelContainer?: React.CSSProperties
) => {
  return (
    <InputLabel
      label={inputLabel}
      labelDescription={labelDescription}
      tooltip={tooltip}
      inputId={`label-${inputId}`}
      customStyle={customStyleLabel}
      isBilling={isBilling}
      isRequired={isRequired}
      extraLabelInfo={extraLabelInfo}
      customContainerStyle={customStyleLabelContainer}
    />
  );
};

const TextInput = ({
  id,
  onChange,
  type,
  inputLabel,
  tooltip,
  labelDescription,
  eleRef,
  statusMessage,
  status = 'default',
  showPassword = false,
  customStyle,
  customInputStyle,
  autoFocus,
  isBilling = false,
  customStyleLabel,
  isLogin = false,
  joinForm = false,
  noBorder = false,
  isRequired = false,
  hideStatusIcon = false,
  showCounter = false,
  maxCount = 0,
  value = '',
  extraLabelInfo,
  customStyleLabelContainer,
  customStatusDescriptionStyle,
  ...rest
}: ITextInputProps): ReactElement => {
  const textInput = useRef(null);
  const [passwordInputType, setPasswordInputType] = useState('password');
  const [hideError, setHideError] = useState(false);
  const darkMode = isDarkMode();

  useEffect(() => {
    if (autoFocus) {
      focusTextInput();
    }
  }, [autoFocus]);

  const focusTextInput = () => {
    if (textInput.current) textInput.current.focus();
  };

  const handleShowPassword = () => {
    setPasswordInputType(passwordInputType === 'text' ? 'password' : 'text');
  };

  const showPasswordToggle = showPassword ? (
    <ShowPassword onClick={handleShowPassword} id="toggle_password">
      {passwordInputType === 'password' ? <Visibility /> : <VisibilityOff />}
    </ShowPassword>
  ) : null;

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  const hasCounterError = value?.length > maxCount;

  const showValueCounter = (
    <Counter hasError={hasCounterError}>
      {
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        value?.length || 0
      }
      /{maxCount}
    </Counter>
  );

  return (
    <InputContainer ref={eleRef} style={customStyle}>
      {(inputLabel || showPassword) && (
        <LabelContainer style={{ color: isLogin ? 'rgba(0,0,0,.65)' : '' }}>
          {inputLabel &&
            renderInputLabel(
              inputLabel,
              labelDescription,
              tooltip,
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              id,
              isBilling,
              customStyleLabel,
              isRequired,
              extraLabelInfo,
              customStyleLabelContainer
            )}
          {showPassword && showPasswordToggle}
          {showCounter && showValueCounter}
        </LabelContainer>
      )}
      <Input className={darkMode && !isLogin ? 'darkmode' : 'lightmode'}>
        <InputStyle
          id={id?.toString()}
          onChange={onChange}
          data-test-id={`text-${id}`}
          type={showPassword ? passwordInputType : type}
          status={status}
          ref={textInput}
          onFocus={() => (isBilling ? setHideError(true) : null)}
          islogin={isLogin.toString()}
          joinForm={joinForm.toString()}
          data-lpignore="true"
          autoComplete="off"
          style={customInputStyle}
          borderless={noBorder ? 'true' : 'false'}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          value={value}
          {...rest}
        />
        {!hideStatusIcon && status !== 'default' ? renderStatusIcon(status) : null}
        {status && <StatusDescription status={status} style={customStatusDescriptionStyle} />}
      </Input>
      {!hideError && statusMessage && status !== 'default' ? (
        <StatusDescription data-test-id={`${status}-${id}`} status={status} style={customStatusDescriptionStyle}>
          {statusMessage}
        </StatusDescription>
      ) : null}
    </InputContainer>
  );
};

export default TextInput;
